import {
  useState,
  useCallback,
  useEffect,
  MouseEvent,
  useLayoutEffect,
} from "react";

import { colorsForIndex } from "../Theme";
import { ForwardedChartHandle } from "app/components/shared/Chart/chart";

export function useHighlightDataset(
  chartRef: React.RefObject<ForwardedChartHandle>,
) {
  const highlightDataset = useCallback(
    (hoveredIndex: number) => {
      const chartInstance = chartRef.current?.getChartInstance();
      if (!chartInstance) {
        return;
      }

      if (chartInstance.getDatasetMeta(hoveredIndex).hidden) {
        return;
      }

      chartInstance.data.datasets.forEach((dataset, index) => {
        if (index === hoveredIndex) {
          dataset.borderColor = colorsForIndex(index).borderColor;
          dataset.backgroundColor = colorsForIndex(index).backgroundColor;
        } else {
          dataset.borderColor = `${colorsForIndex(index).borderColor}20`;
          dataset.backgroundColor = `${colorsForIndex(index).backgroundColor}20`;
        }
      });

      chartInstance.update();
    },
    [chartRef],
  );

  const reset = useCallback(() => {
    const chartInstance = chartRef.current?.getChartInstance();
    if (!chartInstance) {
      return;
    }

    chartInstance.data.datasets.forEach((dataset, index) => {
      dataset.borderColor = colorsForIndex(index).borderColor;
      dataset.backgroundColor = colorsForIndex(index).backgroundColor;
    });

    chartInstance.update();
  }, [chartRef]);

  return (index: number) => ({
    onMouseEnter: () => highlightDataset(index),
    onMouseUp: reset,
    onMouseLeave: reset,
  });
}

export function useToggleDatasets(
  chartRef: React.RefObject<ForwardedChartHandle>,
) {
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());

  useLayoutEffect(() => {
    const chartInstance = chartRef.current?.getChartInstance();
    if (!chartInstance) {
      return;
    }

    chartInstance.data.datasets.forEach((_dataset, datasetIndex) => {
      if (hiddenDatasets.has(datasetIndex)) {
        chartInstance.hide(datasetIndex);
      } else {
        chartInstance.show(datasetIndex);
      }
    });

    chartInstance.update();
  }, [chartRef, hiddenDatasets]);

  const toggleDataset = useCallback(
    (index: number) => {
      const nextHiddenDatasets = new Set(hiddenDatasets);

      if (hiddenDatasets.has(index)) {
        nextHiddenDatasets.delete(index);
      } else {
        nextHiddenDatasets.add(index);
      }

      setHiddenDatasets(nextHiddenDatasets);
    },
    [hiddenDatasets],
  );

  const focusDataset = useCallback(
    (index: number) => {
      const chartInstance = chartRef.current?.getChartInstance();
      if (!chartInstance) {
        return;
      }

      const nextHiddenDatasets = new Set();

      const alreadyFocussed =
        chartInstance.getVisibleDatasetCount() === 1 &&
        !hiddenDatasets.has(index);

      if (!alreadyFocussed) {
        chartInstance.data.datasets.forEach((_dataset, datasetIndex) => {
          if (datasetIndex !== index) {
            nextHiddenDatasets.add(datasetIndex);
          }
        });
      }

      setHiddenDatasets(nextHiddenDatasets);
    },
    [chartRef, hiddenDatasets],
  );

  const isDatasetVisible = useCallback(
    (index) => !hiddenDatasets.has(index),
    [hiddenDatasets],
  );

  const visibilityHandlers = (index: number) => ({
    onClick: (event: MouseEvent) => {
      if (event.altKey) {
        focusDataset(index);
      } else {
        toggleDataset(index);
      }
    },
  });

  return {
    hiddenDatasets,
    isDatasetVisible,
    focusDataset,
    toggleDataset,
    visibilityHandlers,
  };
}
